import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Skeleton } from '../components/ui/skeleton';
import { Checkbox } from '../components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { Input } from '../components/ui/input';
import {
  ArrowLeft,
  Globe,
  MessageSquare,
  FileText,
  Sparkles,
  Calendar,
  Download,
  ListTodo,
  CheckCircle,
  Circle,
  Link2,
  Unlink,
  Search,
  Loader2,
  User,
  Building2,
  X,
  Send,
} from 'lucide-react';
import { format } from 'date-fns';

const API_BASE = (process.env.REACT_APP_BACKEND_URL || "") + '/api';

const LANGUAGES = {
  pl: { name: 'Polski', flag: '🇵🇱' },
  de: { name: 'Deutsch', flag: '🇩🇪' },
  en: { name: 'English', flag: '🇬🇧' },
  es: { name: 'Español', flag: '🇪🇸' },
  fr: { name: 'Français', flag: '🇫🇷' },
  it: { name: 'Italiano', flag: '🇮🇹' },
  uk: { name: 'Українська', flag: '🇺🇦' },
  ru: { name: 'Русский', flag: '🇷🇺' },
  zh: { name: '中文', flag: '🇨🇳' },
  ja: { name: '日本語', flag: '🇯🇵' },
  ko: { name: '한국어', flag: '🇰🇷' },
  ar: { name: 'العربية', flag: '🇸🇦' },
  pt: { name: 'Português', flag: '🇵🇹' },
  nl: { name: 'Nederlands', flag: '🇳🇱' },
  tr: { name: 'Türkçe', flag: '🇹🇷' },
  cs: { name: 'Čeština', flag: '🇨🇿' },
};

const LiveSessionSummary = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const { token, t } = useApp();
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('summary');

  // Contact linking state
  const [linkedContacts, setLinkedContacts] = useState({});
  const [linkingPerson, setLinkingPerson] = useState(null);
  const [contactSearchQuery, setContactSearchQuery] = useState('');
  const [contactSearchResults, setContactSearchResults] = useState([]);
  const [searchingContacts, setSearchingContacts] = useState(false);
  const [linkingInProgress, setLinkingInProgress] = useState(false);

  // AI Chat state
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const chatEndRef = useRef(null);

  useEffect(() => {
    loadSession();
  }, [sessionId]);
  
  const loadSession = async () => {
    try {
      const res = await fetch(`${API_BASE}/live/session/${sessionId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setSession(data);
        if (data.linked_contacts) {
          setLinkedContacts(data.linked_contacts);
        }
      } else {
        toast.error('Session not found');
        navigate('/live');
      }
    } catch (error) {
      toast.error('Failed to load session');
    } finally {
      setLoading(false);
    }
  };

  const handleSearchContacts = async (query) => {
    setContactSearchQuery(query);
    if (query.length < 2) {
      setContactSearchResults([]);
      return;
    }
    setSearchingContacts(true);
    try {
      const res = await fetch(`${API_BASE}/proxy/contacts/search?q=${encodeURIComponent(query)}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setContactSearchResults(data);
      }
    } catch (error) {
      toast.error('Failed to search contacts');
    } finally {
      setSearchingContacts(false);
    }
  };

  const handleLinkContact = async (personName, contact) => {
    setLinkingInProgress(true);
    try {
      const res = await fetch(`${API_BASE}/live/session/${sessionId}/link-contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          speaker_name: personName,
          contact_id: contact.id,
          contact_info: {
            first_name: contact.first_name,
            last_name: contact.last_name,
            company: contact.company,
            email: contact.email,
          },
        }),
      });
      if (res.ok) {
        setLinkedContacts(prev => ({
          ...prev,
          [personName]: {
            contact_id: contact.id,
            contact_info: {
              first_name: contact.first_name,
              last_name: contact.last_name,
              company: contact.company,
              email: contact.email,
            },
          },
        }));
        setLinkingPerson(null);
        setContactSearchQuery('');
        setContactSearchResults([]);
        toast.success(`Linked ${personName} to ${contact.first_name} ${contact.last_name}`);
      } else {
        toast.error('Failed to link contact');
      }
    } catch (error) {
      toast.error('Failed to link contact');
    } finally {
      setLinkingInProgress(false);
    }
  };
  
  const toggleActionItem = async (actionId, completed) => {
    try {
      const res = await fetch(`${API_BASE}/live/session/${sessionId}/actions/${actionId}?completed=${!completed}`, {
        method: 'PUT',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (res.ok) {
        setSession(prev => ({
          ...prev,
          action_items: prev.action_items.map(item => 
            item.id === actionId ? { ...item, completed: !completed } : item
          )
        }));
      }
    } catch (error) {
      toast.error('Failed to update action item');
    }
  };
  
  const handleSendChat = async (e) => {
    e.preventDefault();
    if (!chatInput.trim() || chatLoading) return;

    const userMessage = chatInput.trim();
    setChatMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setChatInput('');
    setChatLoading(true);

    try {
      const res = await fetch(`${API_BASE}/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ message: userMessage, session_id: sessionId }),
      });
      if (res.ok) {
        const data = await res.json();
        setChatMessages(prev => [...prev, { role: 'assistant', content: data.response }]);
      } else {
        toast.error('Failed to get AI response');
      }
    } catch (error) {
      toast.error('Chat error');
    } finally {
      setChatLoading(false);
      setTimeout(() => chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    }
  };

  const exportSession = () => {
    if (!session) return;
    
    let content = `Live Translation Session: ${session.title || sessionId}\n`;
    content += `Date: ${format(new Date(session.created_at), 'PPpp')}\n`;
    content += `Languages: ${LANGUAGES[session.language1]?.name || session.language1} ↔ ${LANGUAGES[session.language2]?.name || session.language2}\n`;
    
    if (session.description) {
      content += `Description: ${session.description}\n`;
    }
    
    if (session.summary) {
      content += `\n--- Summary ---\n\n${session.summary}\n`;
    }
    
    if (session.action_items?.length > 0) {
      content += `\n--- Action Items ---\n\n`;
      session.action_items.forEach((item, i) => {
        content += `${item.completed ? '[x]' : '[ ]'} ${item.text}\n`;
      });
    }
    
    content += `\n--- Conversation ---\n\n`;
    
    session.messages?.forEach(msg => {
      content += `[${format(new Date(msg.timestamp), 'HH:mm:ss')}] ${msg.speaker} (${msg.source_lang}):\n`;
      content += `  Original: ${msg.original_text}\n`;
      content += `  Translation (${msg.target_lang}): ${msg.translated_text}\n\n`;
    });
    
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `conversation-${sessionId}.txt`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Session exported');
  };
  
  if (loading) {
    return (
      <div className="min-h-[calc(100vh-4rem)] bg-background">
        <div className="max-w-4xl mx-auto px-4 py-8">
          <Skeleton className="h-8 w-48 mb-6" />
          <Skeleton className="h-64 w-full rounded-2xl" />
        </div>
      </div>
    );
  }
  
  if (!session) return null;
  
  const completedActions = session.action_items?.filter(a => a.completed).length || 0;
  const totalActions = session.action_items?.length || 0;
  
  return (
    <div className="min-h-[calc(100vh-4rem)] bg-background">
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Back button */}
        <Button
          variant="ghost"
          onClick={() => navigate('/live')}
          className="mb-6 -ml-2 text-muted-foreground hover:text-foreground"
          data-testid="back-to-live-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          {t('meeting.backToDashboard')}
        </Button>
        
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold tracking-tight flex items-center gap-3" data-testid="session-title">
              <Globe className="w-8 h-8 text-violet-600" />
              {session.title || `Session ${sessionId}`}
            </h1>
            <div className="flex flex-wrap items-center gap-3 mt-2 text-muted-foreground">
              <Badge variant="outline" className="font-mono">{sessionId}</Badge>
              <span className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                {format(new Date(session.created_at), 'PPP')}
              </span>
              <span>
                {LANGUAGES[session.language1]?.flag} ↔ {LANGUAGES[session.language2]?.flag}
              </span>
            </div>
            {session.description && (
              <p className="mt-2 text-muted-foreground">{session.description}</p>
            )}
          </div>
          
          <Button onClick={exportSession} variant="outline" className="rounded-full" data-testid="export-btn">
            <Download className="w-4 h-4 mr-2" />
            Export
          </Button>
        </div>
        
        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
          <Card className="rounded-xl">
            <CardContent className="pt-6 text-center">
              <MessageSquare className="w-8 h-8 mx-auto mb-2 text-violet-600" />
              <p className="text-2xl font-bold">{session.messages?.length || 0}</p>
              <p className="text-sm text-muted-foreground">Messages</p>
            </CardContent>
          </Card>
          <Card className="rounded-xl">
            <CardContent className="pt-6 text-center">
              <ListTodo className="w-8 h-8 mx-auto mb-2 text-orange-500" />
              <p className="text-2xl font-bold">{completedActions}/{totalActions}</p>
              <p className="text-sm text-muted-foreground">Action Items</p>
            </CardContent>
          </Card>
          {[
            { key: 'person1', name: session.person1_name, lang: session.language1 },
            { key: 'person2', name: session.person2_name, lang: session.language2 },
          ].map(({ key, name, lang }) => {
            const personName = name || LANGUAGES[lang]?.name || key;
            const linked = linkedContacts[personName];
            return (
              <Card key={key} className="rounded-xl">
                <CardContent className="pt-6 text-center">
                  <div className="text-3xl mb-2">{LANGUAGES[lang]?.flag}</div>
                  <p className="text-sm font-medium">{personName}</p>
                  {linked ? (
                    <div className="mt-2 flex items-center justify-center gap-1 text-xs text-green-600">
                      <Link2 className="w-3 h-3" />
                      <span>{linked.contact_info?.first_name} {linked.contact_info?.last_name}</span>
                      <button
                        onClick={() => {
                          setLinkedContacts(prev => {
                            const next = { ...prev };
                            delete next[personName];
                            return next;
                          });
                        }}
                        className="ml-1 text-muted-foreground hover:text-red-600"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ) : (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="mt-2 h-7 text-xs"
                      onClick={() => {
                        setLinkingPerson(personName);
                        handleSearchContacts(personName);
                      }}
                    >
                      <Link2 className="w-3 h-3 mr-1" />
                      Link Contact
                    </Button>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
        
        {/* Contact search panel */}
        {linkingPerson && (
          <Card className="mb-8 rounded-xl border-violet-200 dark:border-violet-800">
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm font-medium">
                  Link <span className="text-violet-600">{linkingPerson}</span> to ExpoPulse contact
                </p>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 w-7 p-0"
                  onClick={() => {
                    setLinkingPerson(null);
                    setContactSearchQuery('');
                    setContactSearchResults([]);
                  }}
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
              <div className="flex items-center gap-2 mb-3">
                <Search className="w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search contacts by name or company..."
                  value={contactSearchQuery}
                  onChange={(e) => handleSearchContacts(e.target.value)}
                  className="h-9 text-sm"
                  autoFocus
                />
              </div>
              <div className="max-h-48 overflow-y-auto">
                {searchingContacts && (
                  <div className="flex items-center justify-center py-4">
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    <span className="text-sm text-muted-foreground">Searching...</span>
                  </div>
                )}
                {!searchingContacts && contactSearchResults.length === 0 && contactSearchQuery.length >= 2 && (
                  <p className="text-sm text-muted-foreground text-center py-4">No contacts found</p>
                )}
                {!searchingContacts && contactSearchQuery.length < 2 && (
                  <p className="text-sm text-muted-foreground text-center py-4">Type at least 2 characters</p>
                )}
                {contactSearchResults.map((contact) => (
                  <button
                    key={contact.id}
                    onClick={() => handleLinkContact(linkingPerson, contact)}
                    disabled={linkingInProgress}
                    className="w-full text-left px-3 py-2.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors flex items-center gap-3"
                  >
                    <User className="w-4 h-4 text-violet-600 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">
                        {contact.first_name} {contact.last_name}
                      </p>
                      <p className="text-xs text-muted-foreground truncate">
                        {[contact.company, contact.email].filter(Boolean).join(' · ')}
                      </p>
                    </div>
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid w-full grid-cols-4 rounded-xl h-12">
            <TabsTrigger value="summary" className="rounded-lg" data-testid="tab-summary">
              <Sparkles className="w-4 h-4 mr-2" />
              {t('meeting.tabs.summary')}
            </TabsTrigger>
            <TabsTrigger value="actions" className="rounded-lg" data-testid="tab-actions">
              <ListTodo className="w-4 h-4 mr-2" />
              {t('meeting.tabs.actions')}
            </TabsTrigger>
            <TabsTrigger value="transcript" className="rounded-lg" data-testid="tab-transcript">
              <FileText className="w-4 h-4 mr-2" />
              {t('meeting.tabs.transcript')}
            </TabsTrigger>
            <TabsTrigger value="chat" className="rounded-lg" data-testid="tab-chat">
              <MessageSquare className="w-4 h-4 mr-2" />
              {t('meeting.tabs.chat')}
            </TabsTrigger>
          </TabsList>
          
          {/* Summary Tab */}
          <TabsContent value="summary">
            <Card className="rounded-2xl">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-violet-600" />
                  {t('meeting.summary.title')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {session.summary ? (
                  <div className="space-y-6">
                    <div className="prose prose-slate dark:prose-invert max-w-none" data-testid="session-summary">
                      {session.summary.split('\n').map((line, i) => (
                        <p key={i} className="mb-2">{line}</p>
                      ))}
                    </div>

                    {/* Key Topics */}
                    {session.analysis && (() => {
                      const topicsMatch = session.analysis.match(/KEY TOPICS:\s*([\s\S]*?)(?=SENTIMENT:|$)/i);
                      if (!topicsMatch) return null;
                      const topics = topicsMatch[1].split('\n').map(l => l.replace(/^[-•]\s*/, '').trim()).filter(Boolean);
                      if (topics.length === 0) return null;
                      return (
                        <div className="border-t border-slate-200 dark:border-slate-800 pt-4">
                          <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">
                            {t('meeting.summary.keyTopics')}
                          </h4>
                          <div className="flex flex-wrap gap-2">
                            {topics.map((topic, i) => (
                              <Badge key={i} variant="secondary" className="text-sm">{topic}</Badge>
                            ))}
                          </div>
                        </div>
                      );
                    })()}

                    {/* Sentiment */}
                    {session.analysis && (() => {
                      const sentimentMatch = session.analysis.match(/SENTIMENT:\s*([\s\S]*?)$/i);
                      if (!sentimentMatch) return null;
                      const sentiment = sentimentMatch[1].trim();
                      if (!sentiment) return null;
                      return (
                        <div className="border-t border-slate-200 dark:border-slate-800 pt-4">
                          <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">
                            {t('meeting.summary.sentiment')}
                          </h4>
                          <p className="text-foreground leading-relaxed">{sentiment}</p>
                        </div>
                      );
                    })()}
                  </div>
                ) : (
                  <div className="text-center py-12 text-muted-foreground">
                    <Sparkles className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>{t('meeting.summary.noSummary')}</p>
                    <p className="text-sm mt-2">End the session to generate AI analysis</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
          
          {/* Action Items Tab */}
          <TabsContent value="actions">
            <Card className="rounded-2xl">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <ListTodo className="w-5 h-5 text-orange-500" />
                  {t('meeting.actions.title')}
                  {totalActions > 0 && (
                    <Badge variant="secondary" className="ml-2">
                      {completedActions}/{totalActions}
                    </Badge>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {session.action_items?.length > 0 ? (
                  <div className="space-y-3" data-testid="action-items-list">
                    {session.action_items.map((item, index) => (
                      <div 
                        key={item.id} 
                        className={`flex items-start gap-3 p-4 rounded-xl border transition-all ${
                          item.completed 
                            ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800' 
                            : 'bg-slate-50 dark:bg-slate-800/50 border-slate-200 dark:border-slate-700'
                        }`}
                        data-testid={`action-item-${index}`}
                      >
                        <Checkbox 
                          checked={item.completed}
                          onCheckedChange={() => toggleActionItem(item.id, item.completed)}
                          className="mt-0.5"
                          data-testid={`action-checkbox-${index}`}
                        />
                        <div className="flex-1">
                          <p className={`${item.completed ? 'line-through text-muted-foreground' : ''}`}>
                            {item.text}
                          </p>
                          {item.assignee && (
                            <p className="text-sm text-muted-foreground mt-1">
                              Assigned to: {item.assignee}
                            </p>
                          )}
                        </div>
                        {item.completed ? (
                          <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                        ) : (
                          <Circle className="w-5 h-5 text-slate-400 flex-shrink-0" />
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12 text-muted-foreground">
                    <ListTodo className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>{t('meeting.actions.noActions')}</p>
                    <p className="text-sm mt-2">Action items will appear after AI analysis</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
          
          {/* Transcript Tab */}
          <TabsContent value="transcript">
            <Card className="rounded-2xl">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  {t('meeting.transcript.title')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[500px] pr-4">
                  <div className="space-y-4">
                    {session.messages?.map((msg, index) => (
                      <div key={index} className="border-b border-slate-100 dark:border-slate-800 pb-4 last:border-0" data-testid={`message-${index}`}>
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-lg">{LANGUAGES[msg.source_lang]?.flag || '🌐'}</span>
                          <span className="font-medium">{msg.speaker}</span>
                          <span className="text-xs text-muted-foreground">
                            {format(new Date(msg.timestamp), 'HH:mm:ss')}
                          </span>
                        </div>
                        <p className="text-foreground mb-2">{msg.original_text}</p>
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <span>{LANGUAGES[msg.target_lang]?.flag || '🌐'}</span>
                          <span className="italic">{msg.translated_text}</span>
                        </div>
                      </div>
                    ))}

                    {(!session.messages || session.messages.length === 0) && (
                      <div className="text-center py-12 text-muted-foreground">
                        <MessageSquare className="w-12 h-12 mx-auto mb-4 opacity-50" />
                        <p>{t('meeting.transcript.noTranscript')}</p>
                      </div>
                    )}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Chat Tab */}
          <TabsContent value="chat">
            <Card className="rounded-2xl">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MessageSquare className="w-5 h-5 text-violet-600" />
                  {t('meeting.chat.title')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-col h-[500px]">
                  <ScrollArea className="flex-1 pr-4">
                    <div className="space-y-4">
                      {chatMessages.length === 0 && (
                        <div className="text-center py-12 text-muted-foreground">
                          <MessageSquare className="w-12 h-12 mx-auto mb-4 opacity-50" />
                          <p>{t('meeting.chat.placeholder')}</p>
                        </div>
                      )}
                      {chatMessages.map((msg, index) => (
                        <div
                          key={index}
                          className={`flex gap-3 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                          data-testid={`chat-message-${index}`}
                        >
                          <div
                            className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                              msg.role === 'user'
                                ? 'bg-violet-600 text-white'
                                : 'bg-slate-100 dark:bg-slate-800 text-foreground'
                            }`}
                          >
                            <p className="leading-relaxed">{msg.content}</p>
                          </div>
                        </div>
                      ))}
                      {chatLoading && (
                        <div className="flex gap-3 justify-start">
                          <div className="bg-slate-100 dark:bg-slate-800 rounded-2xl px-4 py-3">
                            <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                          </div>
                        </div>
                      )}
                      <div ref={chatEndRef} />
                    </div>
                  </ScrollArea>
                  <form onSubmit={handleSendChat} className="flex gap-2 pt-4 mt-4 border-t border-slate-200 dark:border-slate-800">
                    <Input
                      value={chatInput}
                      onChange={(e) => setChatInput(e.target.value)}
                      placeholder={t('meeting.chat.placeholder')}
                      className="flex-1 h-11 rounded-xl"
                      disabled={chatLoading}
                      data-testid="chat-input"
                    />
                    <Button
                      type="submit"
                      className="h-11 px-6 rounded-xl bg-violet-600 hover:bg-violet-700"
                      disabled={chatLoading || !chatInput.trim()}
                      data-testid="chat-send-button"
                    >
                      {chatLoading ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        <Send className="w-5 h-5" />
                      )}
                    </Button>
                  </form>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default LiveSessionSummary;
